import {
  alpha,
  Avatar,
  Badge,
  Box,
  Card,
  Checkbox,
  ListItemAvatar,
  ListItemText,
  makeStyles,
  Theme,
  Tooltip,
  Typography,
  useTheme,
} from '@material-ui/core';
import clsx from 'clsx';
import { useModal } from 'react-modal-hook';

import ImageViewer from 'components/ImageViewer';
import dayjs from 'services/dayjs';
import {
  AgendamentoInterfaceV2,
  AgendamentoStatusEnum,
  AgendamentoStatusTypes,
} from 'store/reducers/agendamentos';
import getIconTipoCanal from 'utils/getIconTipoCanal';

import AgendamentoMenuAcoes from './AgendamentoMenuAcoes';
import ModalDetalhesAgendamento from './ModalDetalhesAgendamento';

const useStyles = makeStyles(({ spacing, palette }) => ({
  cardRoot: {
    width: '100%',
    boxSizing: 'border-box',
    padding: spacing(0.5, 1),
    cursor: 'pointer',
  },
  cardStatusCancelled: {
    backgroundColor: palette.type === 'light' ? '#FFF5F5' : '#382d2dff',
    border: `1px solid ${palette.type === 'light' ? '#FFD0D0' : '#701919ff'}`,
  },
  cardSelected: {
    '&&': {
      backgroundColor: alpha(palette.primary.main, 0.1),
    },
  },
  rowGrid: {
    display: 'grid',
    alignItems: 'center',
    columnGap: spacing(8),
    gridTemplateColumns: `
      minmax(90px, 250px)   /* Contato */
      minmax(75px, 150px)   /* Agendado para */
      minmax(65px, 130px)   /* Equipe */
      minmax(75px, 200px)   /* Canal */
      minmax(150px, 1fr)    /* Mensagem */
      48px                  /* Menu */
      `,
    width: '100%',
    minWidth: 0,
  },

  cell: {
    minWidth: 0,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },

  contactCellInner: {
    display: 'flex',
    alignItems: 'center',
    gap: spacing(1.5),
    minWidth: 0,
  },

  listItemTextSecondary: {
    minWidth: 0,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  badge: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginLeft: 10,
  },
  badgeColor: ({ color }: { color: string }) => ({
    '& .MuiBadge-badge': {
      backgroundColor: color,
      transform: 'scale(0.5) translate(0, 0)',
    },
  }),
}));

export const getLabel = (status: AgendamentoStatusTypes, theme: Theme) => {
  switch (status) {
    case 'AGENDADO':
      return {
        label: 'Contato agendado',
        color: theme.palette.primary.main,
      };
    case 'CONCLUIDO':
      return {
        label: 'Enviado com sucesso',
        color: theme.palette.success.main,
      };
    case 'NAO_CONCLUIDO':
      return {
        label: 'Falha no envio',
        color: theme.palette.error.main,
      };
    case 'CANCELADO':
      return {
        label: 'Agendamento cancelado',
        color: theme.palette.error.main,
      };
    default:
      return { label: ' - ', color: '' };
  }
};

type AgendamentoListItemProps = {
  item: AgendamentoInterfaceV2;
  itemChecked: boolean;
  selected: boolean;
  onToggleActionSelection: () => void;
  onCheckItem?: (id: string) => void;
  onSuccess?: () => void;
};

function AgendamentoListItem({
  item,
  itemChecked = false,
  selected,
  onToggleActionSelection,
  onCheckItem,
  onSuccess,
}: AgendamentoListItemProps) {
  const theme = useTheme();
  const { label, color } = getLabel(item.status, theme);
  const classes = useStyles({ color });

  const [showModalDetalhesAgendamento, hideModalDetalhesAgendamento] = useModal(
    ({ in: open }) => (
      <ModalDetalhesAgendamento
        open={open}
        onClose={hideModalDetalhesAgendamento}
        agendamento={item}
        onSuccess={onSuccess}
      />
    ),
    [item],
  );

  return (
    <Card
      variant="outlined"
      elevation={0}
      className={clsx(classes.cardRoot, {
        [classes.cardStatusCancelled]:
          item.status === AgendamentoStatusEnum.NAO_CONCLUIDO,
        [classes.cardSelected]: selected,
      })}
      onClick={() => {
        onToggleActionSelection();
        showModalDetalhesAgendamento();
      }}
    >
      <Box className={classes.rowGrid}>
        <Box className={classes.cell}>
          <Box className={classes.contactCellInner}>
            <Box onClick={e => e.stopPropagation()}>
              <Checkbox
                color="primary"
                size="small"
                checked={itemChecked}
                onChange={() => onCheckItem?.(item.id)}
                disabled={item.status === AgendamentoStatusEnum.CANCELADO}
              />
            </Box>

            <Box display="flex" alignItems="center" minWidth={0}>
              <ListItemAvatar style={{ minWidth: 0, marginRight: 8 }}>
                <Box onClick={e => e.stopPropagation()}>
                  <ImageViewer src={item.contact.photoUrl || ''}>
                    <Avatar src={item.contact.photoUrl || ''} />
                  </ImageViewer>
                </Box>
              </ListItemAvatar>

              <ListItemText
                primary="Contato"
                secondary={item.contact.name}
                primaryTypographyProps={{
                  variant: 'caption',
                  color: 'textSecondary',
                }}
                secondaryTypographyProps={{
                  className: classes.listItemTextSecondary,
                  color: 'textPrimary',
                  title: item.contact.name,
                }}
                style={{ margin: 0 }}
              />
            </Box>
          </Box>
        </Box>

        <Box className={classes.cell}>
          <ListItemText
            primary={
              <Box display="flex" alignItems="center" gridGap={4}>
                <Box width={10} height={11}>
                  <Tooltip title={label} arrow placement="top">
                    <Badge
                      overlap="circular"
                      badgeContent=""
                      classes={{
                        root: clsx(classes.badge, {
                          [classes.badgeColor]: color,
                        }),
                      }}
                    />
                  </Tooltip>
                </Box>
                <span style={{ whiteSpace: 'nowrap' }}>Agendado para</span>
              </Box>
            }
            secondary={dayjs(item.scheduledAt).format('DD/MM/YYYY [às] HH:mm')}
            primaryTypographyProps={{
              variant: 'caption',
              color: 'textSecondary',
            }}
            secondaryTypographyProps={{
              className: classes.listItemTextSecondary,
              color: 'textPrimary',
              title: item.scheduledAt,
            }}
            style={{ margin: 0 }}
          />
        </Box>

        <Box className={classes.cell}>
          <ListItemText
            primary="Equipe"
            secondary={item.team.name}
            primaryTypographyProps={{
              variant: 'caption',
              color: 'textSecondary',
            }}
            secondaryTypographyProps={{
              className: classes.listItemTextSecondary,
              color: 'textPrimary',
              title: item.team.name,
            }}
            style={{ margin: 0 }}
          />
        </Box>

        <Box className={classes.cell}>
          <ListItemText
            primary="Canal"
            primaryTypographyProps={{
              variant: 'caption',
              color: 'textSecondary',
            }}
            secondary={
              <Box style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <Box style={{ marginTop: 2 }}>
                  {getIconTipoCanal({
                    tipoCanal: item.channel.type,
                    size: 16,
                    isModoHibrido: item.channel.isHybridMode,
                  })}
                </Box>

                <Box
                  style={{
                    minWidth: 0,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                  title={item.channel.name}
                >
                  {item.channel.name}
                </Box>
              </Box>
            }
            secondaryTypographyProps={{
              component: 'div',
              color: 'textPrimary',
            }}
            style={{ margin: 0 }}
          />
        </Box>

        <Box className={classes.cell} style={{ display: 'block' }}>
          <Typography
            component="div"
            variant="body2"
            color="textSecondary"
            title={item.message}
            style={{
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              minWidth: 0,
            }}
          >
            {item.message}
          </Typography>
        </Box>

        <Box width={48} display="flex" justifyContent="flex-end">
          <AgendamentoMenuAcoes
            agendamento={item}
            onSuccess={onSuccess}
            onToggleSelection={onToggleActionSelection}
          />
        </Box>
      </Box>
    </Card>
  );
}

export default AgendamentoListItem;
